import {
  Injectable,
  signal,
  computed,
  inject,
  PLATFORM_ID,
  effect,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map } from 'rxjs/operators';
import type {
  AddItemToCartPayload,
  AddItemToCartPayloadBackend,
  Cart,
  CartItem,
  UpdateCartItemPayload,
} from '../types/Cart.types';
import { EmailValidationService } from './email-validation.service';
import { environment } from '../environments/environment';
import { ApiMembresiaResponse } from '../types/API.types';
import { CartStatus } from '../pages/cart/carrito.component';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly STORAGE_KEY = 'allas-cart';

  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  // Señales reactivas del carrito
  private cart = signal<Cart>(this.getInitialCart());
  private currentCartId: number | null = null;
  private emailValidationService = inject(EmailValidationService);

  // Observable para suscribirse a cambios del carrito
  cart$ = new BehaviorSubject<Cart>(this.getInitialCart());

  // Computed values para la UI
  cartItems = computed(() => {
    const cart = this.cart();
    return Array.isArray(cart?.items) ? cart.items : [];
  });
  totalItems = computed(() => {
    const cart = this.cart();
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }
    return cart.items.reduce((total, item) => total + (item?.cantidad || 0), 0);
  });
  isEmpty = computed(() => {
    const cart = this.cart();
    return !cart || !Array.isArray(cart.items) || cart.items.length === 0;
  });

  /**
   * Obtiene el observable del carrito para suscribirse a cambios
   * @returns Observable<Cart>
   */
  getCartObservable() {
    return this.cart$.asObservable();
  }

  constructor() {
    // Verificar si hay sesión al inicializar
    this.initializeCart();
  }

  async setCartFromBackend(idUsuarioDigital: number) {
    const cart = await firstValueFrom(
      this.getCartFromBackend(idUsuarioDigital)
    );

    this.setCart(cart);
  }

  setCart(cart: Cart) {
    // Asegurar que items siempre sea un array
    const validCart = {
      ...cart,
      items: Array.isArray(cart?.items) ? cart.items : [],
    };
    this.cart.set(validCart);
    this.saveCartToStorage(validCart);
    this.currentCartId = validCart.id;
    this.cart$.next(validCart);
  }

  getCartFromBackend(idUsuarioDigital: number) {
    const url = `${environment.apiUrl}/v1/carrito/${idUsuarioDigital}`;

    // Headers para evitar que el navegador cachee la respuesta
    const headers = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    };

    return this.http.get<ApiMembresiaResponse<Cart>>(url, { headers }).pipe(
      map((response) => {
        if (!response.data)
          throw new Error('Error obteniendo carrito del backend');

        return response.data;
      }),
      catchError(() => {
        console.error('Error obteniendo carrito del backend');
        throw new Error('Error obteniendo carrito del backend');
      })
    );
  }

  addItemToCartInBackend(payload: AddItemToCartPayloadBackend) {
    if (!this.currentCartId) {
      throw new Error('No hay carrito activo en el backend');
    }

    const url = `${environment.apiUrl}/v1/carrito/detalle`;

    return this.http.post<ApiMembresiaResponse<Cart>>(url, payload).pipe(
      map((response) => {
        if (!response.data)
          throw new Error('Error agregando producto al carrito en backend');

        this.setCart(response.data);

        return response.data;
      }),
      catchError(() => {
        throw new Error('Error agregando producto al carrito en backend');
      })
    );
  }

  removeProductFromCartInBackend(idDetalle: number) {
    if (!this.currentCartId) {
      throw new Error('No hay carrito activo en el backend');
    }

    const url = `${environment.apiUrl}/v1/carrito/detalle/${idDetalle}`;

    return this.http.delete<ApiMembresiaResponse<Cart>>(url).pipe(
      map((response) => {
        if (!response.data)
          throw new Error('Error eliminando producto del carrito en backend');

        this.setCart(response.data);

        return response.data;
      }),
      catchError(() => {
        throw new Error('Error eliminando producto del carrito en backend');
      })
    );
  }

  updateItemInBackend(updatePayload: UpdateCartItemPayload) {
    if (!this.currentCartId) {
      throw new Error('No hay carrito activo en el backend');
    }

    const url = `${environment.apiUrl}/v1/carrito/detalle/${updatePayload.idDetalle}`;

    return this.http.put<ApiMembresiaResponse<Cart>>(url, updatePayload).pipe(
      map((response) => {
        if (!response.data)
          throw new Error('Error actualizando producto en backend');

        this.setCart(response.data);

        return response.data;
      }),
      catchError(() => {
        throw new Error('Error actualizando producto en backend');
      })
    );
  }

  async updateCartStatus(
    cartId: string,
    status: CartStatus,
    nombreContacto: string,
    telefonoContacto: string
  ) {
    try {
      const url = `${environment.apiUrl}/v1/carrito/${cartId}/estado`;
      const body = {
        idEstado: status,
        nombreContacto: nombreContacto,
        telefonoContacto: telefonoContacto,
      };

      await this.http.patch(url, body).toPromise();
    } catch (error) {
      console.error('Error al actualizar el estado del carrito:', error);
    }
  }

  saveCartToStorage(cart: Cart) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      throw new Error('Error saving cart to storage');
    }
  }

  clearCartFromStorage() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.removeItem(this.STORAGE_KEY);
    this.clearCart();
  }

  isUserLoggedIn() {
    const userData = this.emailValidationService.getCurrentUser();
    return !!userData;
  }

  isUserLoggedInAndHasCart() {
    const userData = this.emailValidationService.getCurrentUser();
    return !!userData?.idUsuarioDigital;
  }

  getCurrentUser() {
    return this.emailValidationService.getCurrentUser();
  }

  initializeCart(): void {
    const userData = this.emailValidationService.getCurrentUser();

    if (userData?.idUsuarioDigital) {
      this.setCartFromBackend(userData.idUsuarioDigital);
    } else {
      // Si no hay sesión, cargar desde localStorage
      this.getCartFromStorage();
    }
  }

  getCurrentCartId() {
    return this.currentCartId;
  }

  private getCartFromStorage() {
    if (!isPlatformBrowser(this.platformId)) {
      const initialCart = this.getInitialCart();
      this.setCart(initialCart);
      return initialCart;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        const initialCart = this.getInitialCart();
        this.setCart(initialCart);
        return initialCart;
      }
      const cart = JSON.parse(stored) as Cart;
      this.setCart(cart);
      return cart;
    } catch (error) {
      const initialCart = this.getInitialCart();
      this.setCart(initialCart);
      return initialCart;
    }
  }

  /**
   * Agrega un item al carrito
   */
  async addItemToCart(payload: AddItemToCartPayload) {
    const cart = this.cart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const existingItem = items.find(
      (item) =>
        (item.idGrupo && item.idGrupo === payload.categoria?.id) ||
        (item.codigoProducto && item.codigoProducto === payload.producto?.sku)
    );

    if (existingItem) {
      const isLoggedInAndHasCart = this.isUserLoggedInAndHasCart();

      if (isLoggedInAndHasCart) {
        await firstValueFrom(
          this.updateItemInBackend({
            idDetalle: existingItem.id,
            cantidad: existingItem.cantidad + (payload.cantidad || 1),
            notas: existingItem.notas,
          })
        );
        return;
      }

      this.updateItemInCart({
        ...existingItem,
        cantidad: existingItem.cantidad + (payload.cantidad || 1),
      });

      return;
    }

    if (this.isUserLoggedInAndHasCart()) {
      await firstValueFrom(
        this.addItemToCartInBackend({
          idCarrito: this.currentCartId || 0,
          codigoProducto: payload.producto?.sku || '',
          idGrupo: payload.categoria?.id || 0,
          cantidad: payload.cantidad || 1,
        })
      );

      return;
    }

    const currentCart = this.cart();
    const currentItems = Array.isArray(currentCart?.items)
      ? currentCart.items
      : [];

    const newItem: CartItem = {
      id: currentItems.length + 1,
      idCarrito: this.currentCartId || 0,
      cantidad: 1,
      fechaCreacion: new Date(),
      producto: payload.producto,
      grupo: payload.categoria,
      idGrupo: payload.categoria?.id,
      notas: '',
    };

    this.setCart({
      ...currentCart,
      items: [...currentItems, newItem],
    });
  }

  /**
   * Actualiza un item en el carrito
   */
  async updateItemInCart(payload: CartItem) {
    const cart = structuredClone(this.cart());

    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    cart.items.forEach((item) => {
      if (item.id === payload.id) {
        item.cantidad = payload.cantidad;
        item.notas = payload.notas;
      }
    });

    this.setCart(cart);

    if (this.isUserLoggedInAndHasCart()) {
      const item = cart.items.find((item) => item.id === payload.id);
      if (!item) return;
      await firstValueFrom(
        this.updateItemInBackend({
          idDetalle: item.id,
          cantidad: item.cantidad,
          notas: item.notas,
        })
      );
    }
  }

  async updateItemInCartFromId(
    idDetalle: number,
    cantidad: number,
    notas?: string
  ) {
    const cart = this.cart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const item = items.find((item) => item.id === idDetalle);

    if (!item) return;

    await this.updateItemInCart({
      ...item,
      cantidad: cantidad,
      notas: notas,
    });
  }

  /**
   * Elimina un item del carrito
   */
  async removeItemFromCart(id: number) {
    const cart = structuredClone(this.cart());

    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    cart.items = cart.items.filter((item) => item.id !== id);

    this.setCart(cart);

    const isLoggedInAndHasCart = this.isUserLoggedInAndHasCart();

    if (isLoggedInAndHasCart) {
      await firstValueFrom(this.removeProductFromCartInBackend(id));
    }
  }

  isItemInCart(idDetalle: number): boolean {
    const cart = this.cart();
    if (!cart || !Array.isArray(cart.items)) {
      return false;
    }
    return cart.items.some((item) => item.id === idDetalle);
  }

  getQuantityInCart(idDetalle: number): number {
    const cart = this.cart();
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }
    return cart.items.find((item) => item.id === idDetalle)?.cantidad || 0;
  }

  /**
   * Limpia todo el carrito
   */
  clearCart(): void {
    this.setCart(this.getInitialCart());

    // Limpiar también el carrito del backend
    this.currentCartId = null;
  }

  /**
   * Obtiene el carrito inicial sin modificar el signal
   */
  private getInitialCart(): Cart {
    return {
      id: -1,
      idUsuarioDigital: 0,
      idVehiculoSeleccionado: null,
      sucursalSeleccionada: null,
      nombreContacto: null,
      telefonoContacto: null,
      fechaCreacion: new Date(),
      idEstado: 1,
      items: [],
    };
  }

  getDefaultVehicle() {
    return this.cart().idVehiculoSeleccionado;
  }
}
